import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { doCancelJobOrder, doGetJobOrder } from '../../modules/jobOrders/stores/thunk';
import { Col, Divider, Popover, Row, Spin, Steps, Tooltip, notification, Image } from 'antd';
import BreadCrumb from '../breadcrumbs';
import FormTitleHeader from '../Header/FormTitleHeader';
import './JobOrderDetailPage.scss';
import { getProgressStatus, imageMaker } from '../../utils/common';
import Pickup from '../../Icons/pickup';
import Dropoff from '../../Icons/dropoff';
import DefaultMapbox from '../Mapbox/DefaultMapbox';
import {
  DAYTIME_FRONT_FORMAT,
  DAY_INPUT_FORMAT,
  ITEM_STATUSES,
  JOB_ORDER_STATUSES,
  UNIT_TYPE_DISPLAY,
} from '../../constants';
import { formatClientDeliveryWindowOption } from '../../utils/clientDeliveryWindowLabel';
import { formatCreatedBy } from '../../utils/createdByLabel';
import moment from 'moment';
import { ReactSVG } from 'react-svg';
import { EllipsisOutlined } from '@ant-design/icons';
import { getCurrentDriverLocation, subscribeToDriverLocation } from '../../services/realtimeLocationApi';
// import { Document, Page, pdfjs } from 'react-pdf';
// import 'react-pdf/dist/Page/AnnotationLayer.css';

// pdfjs.GlobalWorkerOptions.workerSrc = new URL('pdfjs-dist/build/pdf.worker.min.js', import.meta.url).toString();

const JobOrderDetailPage = () => {
  const dispatch = useDispatch();
  const [api, contextHolder] = notification.useNotification();
  const jobOrder = useSelector((state) => state.jobOrder.jobOrderDetail);
  const isFetching = useSelector((state) => state.jobOrder.isFetching);
  const status = useSelector((state) => state.jobOrder.status);
  const action = useSelector((state) => state.jobOrder.action);
  const [polylines, setPolylines] = useState([]);
  const [markers, setMarkers] = useState([]);
  const [driverLocation, setDriverLocation] = useState(null);
  const navigate = useNavigate();

  let { jobOrderId } = useParams();

  useEffect(() => {
    dispatch(doGetJobOrder(jobOrderId));
  }, []);

  useEffect(() => {
    if (action === 'getOne' && ['succeeded', 'failed'].includes(status) && !jobOrder) {
      navigate('/job-orders');
    }
  }, [action, status, jobOrder]);

  useEffect(() => {
    if (action === 'cancel' && ['succeeded'].includes(status)) {
      dispatch(doGetJobOrder(jobOrderId));
      api.info({
        message: 'Job Order Canceled',
        description: 'The Job Order has been successfully canceled.',
      });
    }
  }, [action, status]);

  useEffect(() => {
    if (!jobOrder) return;

    if (jobOrder.polyline) {
      setPolylines([{ polyline: jobOrder.polyline, strokeColor: '#1961E4', strokeWeight: 4 }]);
    }

    const newMarkers = [];

    if (jobOrder.pickupAddressLat && jobOrder.pickupAddressLng) {
      newMarkers.push({
        id: 1,
        content: <img src={imageMaker(Pickup())} style={{ maxWidth: 40 }} />,
        latitude: Number(jobOrder.pickupAddressLat),
        longitude: Number(jobOrder.pickupAddressLng),
        offsetX: 0,
        offsetY: 0,
        className: 'order-marker',
      });
    }

    if (jobOrder.deliveryAddressLat && jobOrder.deliveryAddressLng) {
      newMarkers.push({
        id: 2,
        content: <img src={imageMaker(Dropoff())} style={{ maxWidth: 40 }} />,
        latitude: Number(jobOrder.deliveryAddressLat),
        longitude: Number(jobOrder.deliveryAddressLng),
        offsetX: 0,
        offsetY: 0,
        className: 'order-marker',
      });
    }

    if (jobOrder.driverId && driverLocation && !['COMPLETED', 'CANCELLED', 'ERROR', 'PAID'].includes(jobOrder.status)) {
      newMarkers.push({
        id: 3,
        content: (
          <img src="/icons/driver-maker.png" className="order-marker-driver" alt="Driver" style={{ maxWidth: 40 }} />
        ),
        latitude: driverLocation.lat,
        longitude: driverLocation.lng,
        offsetX: 0,
        offsetY: 0,
        className: 'order-marker driver-marker',
      });
    } else {
      const latestProximityLogWithCoords = jobOrder.proximityLogs
        ?.slice()
        .reverse()
        .find((log) => log.addressLat && log.addressLng);

      if (latestProximityLogWithCoords) {
        newMarkers.push({
          id: 3,
          content: <ReactSVG className="order-marker-driver" src="/icons/truck.svg" />,
          latitude: Number(latestProximityLogWithCoords.addressLat),
          longitude: Number(latestProximityLogWithCoords.addressLng),
          offsetX: 0,
          offsetY: 0,
          className: 'order-marker driver-marker',
        });
      }
    }

    setMarkers(newMarkers);
  }, [jobOrder, driverLocation]);

  useEffect(() => {
    if (!jobOrder) return;

    const driverId = jobOrder.driver?.user?.id;
    if (!driverId) return;

    let isCancelled = false;

    (async () => {
      try {
        const loc = await getCurrentDriverLocation(driverId);
        if (!isCancelled && loc) {
          setDriverLocation(loc);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Failed to get current driver location:', err);
      }
    })();

    const unsubscribe = subscribeToDriverLocation(driverId, (loc) => {
      if (!isCancelled && loc) {
        setDriverLocation(loc);
      }
    });

    return () => {
      isCancelled = true;
      unsubscribe();
    };
  }, [jobOrder]);

  const cancelJobOrder = async () => {
    dispatch(doCancelJobOrder(jobOrderId));
  };

  const breadCrumbs = [{ text: 'JOB ORDERS', url: '/job-orders' }, { text: jobOrderId }];

  if (!jobOrder) return <></>;
  const statusDatetime = (status) => jobOrder.logs?.find((l) => l.status === status)?.createdAt;
  const pickupArrived = statusDatetime('PICKUP_ARRIVED');
  const deliveryCompleted = statusDatetime('DELIVERY_COMPLETED') || jobOrder.completedAt;
  const completionTime =
    pickupArrived && deliveryCompleted
      ? `${moment(deliveryCompleted).diff(moment(pickupArrived), 'minutes')} mins`
      : '-';

  const itemsStatus = [
    {
      title: <div className="jobOrderStatus PICKUP_ARRIVED">ARRIVED AT PICKUP</div>,
      description:
        statusDatetime('PICKUP_ARRIVED') && moment(statusDatetime('PICKUP_ARRIVED')).format(DAYTIME_FRONT_FORMAT),
    },
    {
      title: <div className="jobOrderStatus PICKUP_COMPLETED">PICKUP COMPLETED</div>,
      description:
        statusDatetime('PICKUP_COMPLETED') && moment(statusDatetime('PICKUP_COMPLETED')).format(DAYTIME_FRONT_FORMAT),
    },
    {
      title: <div className="jobOrderStatus DELIVERY_ARRIVED">ARRIVED AT DELIVERY</div>,
      description:
        statusDatetime('DELIVERY_ARRIVED') && moment(statusDatetime('DELIVERY_ARRIVED')).format(DAYTIME_FRONT_FORMAT),
    },
    {
      title: <div className="jobOrderStatus DELIVERY_COMPLETED">DELIVERY COMPLETED</div>,
      description:
        statusDatetime('DELIVERY_COMPLETED') &&
        moment(statusDatetime('DELIVERY_COMPLETED')).format(DAYTIME_FRONT_FORMAT),
    },
  ];

  if (!jobOrder.driverId) {
    itemsStatus.unshift({
      title: <div className="jobOrderStatus PENDING">PENDING</div>,
      description: statusDatetime('PENDING') && moment(statusDatetime('PENDING')).format(DAYTIME_FRONT_FORMAT),
    });
  } else {
    itemsStatus.unshift({
      title: <div className="jobOrderStatus ACCEPTED">ACCEPTED</div>,
      description: statusDatetime('ACCEPTED') && moment(statusDatetime('ACCEPTED')).format(DAYTIME_FRONT_FORMAT),
    });
  }

  const content = (
    <div className="cancel-job-detail">
      <div className="action" onClick={cancelJobOrder}>
        <ReactSVG className="icon" src="/icons/cancel.svg" />
        <p>Cancel Job</p>
      </div>
    </div>
  );

  const renderDeliveryMedia = (media, type) => {
    const mediaTypeLabel = type === 'signature' ? 'Signature' : 'Proof of Delivery';

    return (
      <Image.PreviewGroup>
        {media?.map((item, index) => {
          const fileUrl = item?.url;
          if (!fileUrl) return null;

          const isPdf = fileUrl.toLowerCase().endsWith('.pdf') || item?.key?.toLowerCase().endsWith('.pdf');

          return (
            <div key={item?.id || `${type}-${index}`} className="media-item">
              {isPdf ? (
                <a href={fileUrl} target="_blank" rel="noopener noreferrer" className="media-preview-link">
                  <div className="pdf-preview-box">
                    <ReactSVG className="pdf-icon" src="/icons/pdf.svg" />
                    <span>PDF Document</span>
                  </div>
                  <div className="media-overlay">
                    <ReactSVG className="eye-icon" src="/icons/preview.svg" />
                  </div>
                </a>
              ) : (
                <div className="media-preview-link">
                  <Image
                    src={fileUrl}
                    crossOrigin="anonymous"
                    alt={`${mediaTypeLabel} ${index + 1}`}
                    preview={{
                      mask: (
                        <div className="media-overlay" style={{ opacity: 1 }}>
                          <ReactSVG className="eye-icon" src="/icons/preview.svg" />
                        </div>
                      ),
                    }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </Image.PreviewGroup>
    );
  };

  console.log({ proximityLogs: jobOrder.proximityLogs });

  return (
    <Spin spinning={isFetching}>
      {contextHolder}
      <div className="Content JobOrderDetailPage">
        <BreadCrumb breadCrumbs={breadCrumbs} />
        <div className="wrapper">
          <div className="header">
            <FormTitleHeader title="Order Details" />
            <div className="action-header">
              <Popover placement="bottom" content={content} trigger="click">
                <EllipsisOutlined />
              </Popover>
            </div>
          </div>
          <div className="map">
            <DefaultMapbox markers={markers} className="mapbox-order-detail" polylines={polylines} />
          </div>
          <div className="order-info">
            <div className="header">
              <div className="header-left"></div>
              <div className="header-right">
                <span className={'jobOrderStatus ' + jobOrder?.status?.toUpperCase()}>
                  <div className="circle"></div>
                  {JOB_ORDER_STATUSES[jobOrder?.status]}
                </span>
                {jobOrder?.isSpecialDelivery && (
                  <span
                    style={{
                      backgroundColor: '#D9534F',
                      color: '#fff',
                      padding: '4px 10px',
                      borderRadius: 6,
                      marginLeft: 8,
                      fontSize: 12,
                      fontWeight: 600,
                    }}
                  >
                    Special Delivery
                  </span>
                )}
              </div>
            </div>
            <div className="cost">
              <div className="cost-container">
                <b>Base Cost</b>
                <div className="price-wrapper">
                  <div className="price">
                    <h4>${jobOrder?.amount ? parseFloat(Number(jobOrder.amount).toFixed(2)) : '0'}</h4>
                  </div>
                </div>
              </div>
              <div className="cost-container">
                <b>GST </b>
                <div className="price-wrapper">
                  <div className="price">
                    <h4>
                      $
                      {jobOrder?.amount
                        ? parseFloat((Number(jobOrder.amount) * (jobOrder?.gstRate || 0)).toFixed(2))
                        : '0'}
                    </h4>
                  </div>
                </div>
              </div>
              <div className="cost-container">
                <b>Driver Payment (62%)</b>
                <div className="price-wrapper">
                  <div className="price">
                    <h4>${jobOrder?.amount ? parseFloat((Number(jobOrder.amount) * 0.62).toFixed(2)) : '0'}</h4>
                  </div>
                </div>
              </div>
              {jobOrder?.driverGstAmount != null && Number(jobOrder.driverGstAmount) > 0 && (
                <div className="cost-container">
                  <b>Driver GST (5%)</b>
                  <div className="price-wrapper">
                    <div className="price">
                      <h4>${parseFloat(Number(jobOrder.driverGstAmount).toFixed(2))}</h4>
                    </div>
                  </div>
                </div>
              )}
              <div className="cost-container">
                <b>Total Cost</b>
                <div className="price-wrapper">
                  <div className="price">
                    <h4>
                      $
                      {jobOrder?.amount
                        ? parseFloat((Number(jobOrder.amount) * (1 + (jobOrder?.gstRate || 0))).toFixed(2))
                        : '0'}
                    </h4>
                  </div>
                </div>
                {/* <Button size="large" type="link">
                  View Cost Breakdown
                  <ReactSVG className="icon" src="/icons/list_alt.svg" />
                </Button> */}
              </div>
            </div>
            <div className="order-time-info">
              <Row>
                <Col span={8}>
                  <div className="child">
                    <div className="child-content">
                      <label>Date</label>
                      <p>{moment(jobOrder.deliveryDate).format(DAY_INPUT_FORMAT)}</p>
                    </div>
                    <div className="divider-left"></div>
                  </div>
                </Col>
                <Col span={8}>
                  <div className="child">
                    <div className="child-content">
                      <label>Completion Time</label>
                      <p>{completionTime}</p>
                    </div>
                    <div className="divider-left"></div>
                  </div>
                </Col>
                <Col span={8}>
                  <div className="child">
                    <div className="child-content">
                      <label>Distance </label>
                      <p>{jobOrder.distance ? (jobOrder.distance / 1000).toFixed(2) + ' km' : '-'}</p>
                    </div>
                  </div>
                </Col>
              </Row>
              <Row style={{ marginTop: 16 }}>
                <Col span={8}>
                  <div className="child">
                    <div className="child-content">
                      <label>Client time option</label>
                      <p>{formatClientDeliveryWindowOption(jobOrder.clientDeliveryWindowOption)}</p>
                    </div>
                    <div className="divider-left"></div>
                  </div>
                </Col>
                <Col span={8}>
                  <div className="child">
                    <div className="child-content">
                      <label>Pickup time</label>
                      <p>{jobOrder.pickupTime || '—'}</p>
                    </div>
                    <div className="divider-left"></div>
                  </div>
                </Col>
                <Col span={8}>
                  <div className="child">
                    <div className="child-content">
                      <label>Delivery window</label>
                      <p>
                        {jobOrder.deliveryStartTime && jobOrder.deliveryEndTime
                          ? `${jobOrder.deliveryStartTime} – ${jobOrder.deliveryEndTime}`
                          : '—'}
                      </p>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
            {jobOrder?.isSpecialDelivery && (
              <>
                <Divider />
                <div className="description">
                  <b>Special Delivery Details</b>
                  <p>{jobOrder.specialDeliveryDetails || '-'}</p>
                </div>
              </>
            )}
            <div className="addresses">
              <div className="address">
                <div className="circle-pickup"></div>
                <p>{jobOrder.pickupAddress}</p>
              </div>
              <div className="little-circles">
                <div className="little-circle"></div>
                <div className="little-circle"></div>
                <div className="little-circle"></div>
              </div>
              <div className="address">
                <div className="circle-delivery"></div>
                <p>{jobOrder.deliveryAddress}</p>
              </div>
            </div>
            <Divider />
            <div className="description">
              <b>Contact Details for Pickup</b>
              <p>
                name : {jobOrder.contactName} - phone: {jobOrder.contactPhone}
              </p>
            </div>
            <div className="description" style={{ paddingTop: 0 }}>
              <b>Contact Details for Drop-off</b>
              <p>
                name : {jobOrder.deliveryContactName} - phone: {jobOrder.deliveryContactPhone}
              </p>
            </div>
            <Divider />
            <div className="order-instruction">
              <b>Pickup Instruction</b>
              <p>{jobOrder.instruction}</p>
              <b>Receipt:</b>
              <div className="files">
                <Image.PreviewGroup>
                  {jobOrder.files?.map((file) => {
                    const type = file.key.split('.').pop()?.toLowerCase();
                    const isImage = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(type);

                    if (isImage) {
                      return (
                        <div key={file.id + 'file'} className="preview-wrapper">
                          <Image
                            src={file.url}
                            crossOrigin="anonymous"
                            preview={{
                              mask: (
                                <Tooltip placement="top" title={file.key}>
                                  <div className="mask-content">
                                    <ReactSVG className="icon" src="/icons/preview.svg" />
                                  </div>
                                </Tooltip>
                              ),
                            }}
                          />
                        </div>
                      );
                    }

                    return (
                      <Tooltip key={file.id + 'file'} placement="top" title={file.key}>
                        <div className="preview-wrapper doc-preview" onClick={() => window.open(file.url, '_blank')}>
                          <div className="pdf-preview-box">
                            <ReactSVG
                              className="pdf-icon"
                              src={`/icons/${['pdf', 'csv', 'xlsx'].includes(type) ? type : 'pdf'}.svg`}
                            />
                            <span>{type?.toUpperCase() || 'FILE'}</span>
                          </div>
                        </div>
                      </Tooltip>
                    );
                  })}
                </Image.PreviewGroup>
              </div>
            </div>
            <Divider />
            <div className="items">
              <b>Item(s) for pickup</b>
              {jobOrder.packages.length > 0 &&
                jobOrder.packages.map((pac, i) => (
                  <>
                    <div key={pac.id + 'package'} className="item">
                      <div className="item-text">
                        <p>
                          {i + 1}. {pac.name}
                        </p>
                        <p>
                          {pac.length || 'N/A'} x {pac.width || 'N/A'} x {pac.height || 'N/A'}{' '}
                          {UNIT_TYPE_DISPLAY[pac.unit]} | {pac.weight || 'N/A'} kg | Qty:{' '}
                          {!!pac.actualQuantity && pac.actualQuantity !== pac.quantity && (
                            <>
                              <b style={{ color: 'red' }}>{pac.actualQuantity}</b>
                              {' / '}
                            </>
                          )}
                          {pac.quantity || 'N/A'}
                        </p>
                      </div>
                      <span className={`item-status ${pac.status}`}>{ITEM_STATUSES[pac.status] || pac.status}</span>
                    </div>
                    {i != 0 && i !== jobOrder.packages.length - 1 && <Divider />}
                  </>
                ))}
            </div>
            <Divider />
            <div className="description">
              <b>Drop-off Instruction</b>
              <p>{jobOrder.deliveryInstruction || '-'}</p>
            </div>
            <Divider />
            <div className="description">
              <b>Created By</b>
              <p>{formatCreatedBy(jobOrder)}</p>
            </div>
            {jobOrder?.user?.phone && (
              <div className="description">
                <b>Customer Phone</b>
                <p>{jobOrder?.user?.phone}</p>
              </div>
            )}
            <div className="rating-section">
              <b>Rate & Review</b>
              <div className="rating-container">
                <div className="star-rating">
                  <span className="rating-value">{jobOrder?.rating}</span>
                  <ReactSVG className="star-icon" src="/icons/star.svg" />
                </div>
                <div className="comment-field">
                  <label>Review Comment</label>
                  <p>{jobOrder?.reviewComment || '-'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="wrapper">
          <FormTitleHeader title="Job Details" />
          <Divider />
          <div className="statuses-log white border-bottom">
            <div className="header">
              <div className="Info Rider">
                <label>Driver: </label>
                <div className="Text">
                  {jobOrder.driver?.user.firstName
                    ? jobOrder.driver?.user.firstName + ' ' + jobOrder.driver?.user.lastName
                    : jobOrder.driver?.user.name || 'Unassigned'}
                  {jobOrder.driver?.user.phone && <span> - {jobOrder.driver?.user.phone}</span>}
                </div>
              </div>
            </div>
            <div className="statuses">
              <Steps
                status="finish"
                current={getProgressStatus(jobOrder.status)}
                labelPlacement="vertical"
                items={itemsStatus}
              />
            </div>
          </div>
          <Divider />
          <div className="DeliveryDetail">
            <Row>
              <Col span={12}>
                <b>Proof of Delivery</b>
                <div className="photos">
                  <div className="value">{renderDeliveryMedia(jobOrder?.proofs, 'pod')}</div>
                </div>
              </Col>
              <Col span={12}>
                <b>Customer Signature</b>
                <div className="photos signature border-left">
                  <div className="value">{renderDeliveryMedia(jobOrder?.signatures, 'signature')}</div>
                </div>
              </Col>
            </Row>
          </div>
          <Divider />
          <div className="description">
            <b>Delivery Note</b>
            <p>{jobOrder.deliveryNote || '-'}</p>
          </div>
          {jobOrder.proximityLogs?.length > 0 && (
            <>
              <Divider />
              <div className="proximity-logs">
                <b>Proximity logs (driver was near location)</b>
                <p className="proximity-logs-desc">
                  Time and location when the driver was within range of pickup or delivery.
                </p>
                <div className="proximity-logs-list">
                  {jobOrder.proximityLogs.map((log) => (
                    <div key={log.id} className="proximity-log-item">
                      <div className="proximity-log-type">
                        <span className={`badge ${log.type}`}>
                          {log.type.toLowerCase() === 'pickup' ? 'Near Pickup' : 'Near Delivery'}
                        </span>
                        <span className="proximity-log-time">{moment(log.createdAt).format(DAYTIME_FRONT_FORMAT)}</span>
                        {log.addressLat && log.addressLng && (
                          <a
                            href={`https://www.google.com/maps?q=${log.addressLat},${log.addressLng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View on map
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default JobOrderDetailPage;
